import { useMemo } from 'react';

import { useGetApiV1TutoraiDocuments } from '@/shared/api/generated';

import type { TutorDocument } from '../types/tutorAi';
import { normalizeDocument } from '../utils/normalizeTutorAi';

/**
 * Documentos de contexto de uma turma.
 *
 * Sem `refetchInterval`: a importação é sincrônica no endpoint de import — quando ele
 * responde, o status final já está definido. Não há processamento assíncrono a acompanhar.
 *
 * @param classStudentId - Id da turma
 * @returns Documentos normalizados, estados de carregamento/erro e `refetch` para nova tentativa
 *
 * @example
 * ```tsx
 * const { documents, isLoading } = useTutorClassDocuments(9020);
 * ```
 */
export function useTutorClassDocuments(classStudentId: number) {
  const query = useGetApiV1TutoraiDocuments({ classStudentId });

  const documents: readonly TutorDocument[] = useMemo(
    () => (query.data ?? []).map(normalizeDocument),
    [query.data],
  );

  return {
    documents,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}
